import {syntaxTree} from '@codemirror/language'
import {type Extension, RangeSetBuilder} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView, ViewPlugin, type ViewUpdate} from '@codemirror/view'

import styles from './active-block.module.css'

type HEADING_LEVELS = 1 | 2 | 3 | 4 | 5 | 6

// Wraps the text and delimiter content
const headingContainerMarkDecorations = {
  1: Decoration.mark({tagName: 'h1'}),
  2: Decoration.mark({tagName: 'h2'}),
  3: Decoration.mark({tagName: 'h3'}),
  4: Decoration.mark({tagName: 'h4'}),
  5: Decoration.mark({tagName: 'h5'}),
  6: Decoration.mark({tagName: 'h6'}),
} as const

/**
 * Extract heading level from various possible node names
 */
function getHeadingLevel(nodeName: string): HEADING_LEVELS | null {
  // ATX headings: ATXHeading1, ATXHeading2, etc.
  const atxMatch = nodeName.match(/ATXHeading([1-6])/)
  if (atxMatch) {
    return parseInt(atxMatch[1]!) as HEADING_LEVELS
  }

  // Setext headings: SetextHeading1, SetextHeading2
  const setextMatch = nodeName.match(/SetextHeading([1-2])/)
  if (setextMatch) {
    return parseInt(setextMatch[1]!) as HEADING_LEVELS
  }

  // Generic heading patterns
  const genericMatch = nodeName.match(/Heading([1-6])/)
  if (genericMatch) {
    return parseInt(genericMatch[1]!) as HEADING_LEVELS
  }

  return null
}

/**
 * Check if a node represents a heading delimiter/marker
 */
function isHeadingMarker(nodeName: string): boolean {
  return nodeName === 'HeaderMark'
}

/**
 * Builds heading decorations using the syntax tree instead of regex
 */
function buildHeadingDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>()
  const tree = syntaxTree(view.state)

  // Only process visible ranges for performance
  for (const {from, to} of view.visibleRanges) {
    tree.iterate({
      from,
      to,
      enter: node => {
        const level = getHeadingLevel(node.name)
        if (level) {
          const headingStart = node.from
          const headingEnd = node.to
          const isSetext = node.name.includes('SetextHeading')

          // Look for child nodes to find text and markers separately
          tree.iterate({
            from: node.from,
            to: node.to,
            enter: child => {
              if (isHeadingMarker(child.name)) {
                if (isSetext) {
                  builder.add(headingStart, child.from, headingContainerMarkDecorations[level])
                  builder.add(child.from, child.to, Decoration.mark({tagName: `h${level}`, class: styles.delimiter}))
                } else {
                  builder.add(headingStart, headingEnd, headingContainerMarkDecorations[level])
                  builder.add(child.from, child.to, Decoration.mark({class: styles.delimiter}))
                }
              }
            },
          })
        }
      },
    })
  }

  return builder.finish()
}

/**
 * View plugin that provides heading decorations using syntax tree
 */
const headingDecorationPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet

    constructor(view: EditorView) {
      this.decorations = buildHeadingDecorations(view)
    }

    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildHeadingDecorations(update.view)
      }
    }
  },
  {
    decorations: v => v.decorations,
  },
)

/**
 * Extension that provides heading styling for markdown using syntax tree
 */
export const headingExtension: Extension = [headingDecorationPlugin]
